import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import orgManagementService from '../apiServices/orgManagementService';
import coreAPIService from '../apiServices/coreAPIService';
import { CTW_API_ENDPOINT } from '../utils';
import { useAuthOrg } from './AuthOrgContext';

interface AdAccount {
    id: string;
    name: string;
}

interface FbPage {
    id: string;
    name: string;
}

interface Waba {
    clientId: string;
    subdomain: string;
    wabaId: string;
    wabaNumber: string;
    phoneNumberId: string;
    metaConversionApiStatus: boolean;
    accessTokenType: string;
    datasetId: string;
}

interface OrgWabaDetails {
    orgId: string;
    orgName: string;
    wabaDetails: Waba[];
}

interface MetaSetupContextType {
    // Ad Account
    selectedAdAccount: AdAccount | null;
    setSelectedAdAccount: (account: AdAccount | null) => void;

    // Facebook Page
    selectedFbPage: FbPage | null;
    setSelectedFbPage: (page: FbPage | null) => void;

    // WABA
    selectedWaba: Waba | null;
    setSelectedWaba: (waba: Waba | null) => void;
    wabaList: Waba[];
    orgWabaData: OrgWabaDetails[];
    loadingWaba: boolean;
    fetchWabaDetails: () => Promise<void>;
    fetchAdAccountDetails: () => Promise<void>;
    adAccountList: AdAccount[];
    loadingAdAccounts: boolean;
    metaConnectStatus: boolean;
    setMetaConnectStatus: (status: boolean) => void;
    fetchMetaStatus: () => Promise<void>;

    // Clear all selections
    clearSelections: () => void;
}

const MetaSetupContext = createContext<MetaSetupContextType | undefined>(undefined);

export const MetaSetupProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [selectedAdAccount, setSelectedAdAccount] = useState<AdAccount | null>(null);
    const [selectedFbPage, setSelectedFbPage] = useState<FbPage | null>(null);
    const [selectedWaba, setSelectedWaba] = useState<Waba | null>(null);
    const [wabaList, setWabaList] = useState<Waba[]>([]);
    const [orgWabaData, setOrgWabaData] = useState<OrgWabaDetails[]>([]);
    const [loadingWaba, setLoadingWaba] = useState(false);
    const [loadingAdAccounts, setLoadingAdAccounts] = useState(false);
    const [adAccountList, setAdAccountList] = useState<AdAccount[]>([]);
    const [metaConnectStatus, setMetaConnectStatus] = useState(false);
    const { isAuthenticated } = useAuthOrg();

    const fetchWabaDetails = useCallback(async () => {
        try {
            setLoadingWaba(true);
            const response = await orgManagementService.getWabaDetails();
            if (response?.meta?.code === 200 && response.data) {
                setOrgWabaData(response.data as any);
                const allWaba: Waba[] = [];
                response.data.forEach(org => {
                    org.wabaDetails.forEach(waba => {
                        allWaba.push(waba as any);
                    });
                });
                setWabaList(allWaba);
            }
        } catch (error) {
            console.error('Error fetching WABA details:', error);
        } finally {
            setLoadingWaba(false);
        }
    }, []);

    const fetchAdAccountDetails = useCallback(async () => {
        try {
            setLoadingAdAccounts(true);
            const res = await coreAPIService.get<any>(CTW_API_ENDPOINT, 'v2/org/ad-accounts');
            if (res?.meta?.code === 200 && res.data) {
                setAdAccountList(res.data);
                // Find default ad account
                const defaultAccount = res.data.find((acc: any) => acc.is_default || acc.isDefault);
                if (defaultAccount) {
                    setSelectedAdAccount({ id: defaultAccount.id, name: defaultAccount.name });
                } else if (res.data.length > 0) {
                    // Fallback to first one if none selected yet
                    setSelectedAdAccount(prev => prev ? prev : { id: res.data[0].id, name: res.data[0].name });
                }
            }
        } catch (error) {
            console.error('Error fetching ad accounts:', error);
        } finally {
            setLoadingAdAccounts(false);
        }
    }, []);

    const fetchMetaStatus = useCallback(async () => {
        try {
            const res = await coreAPIService.get<any>(CTW_API_ENDPOINT, 'v2/org/connection-status');
            if (res?.meta?.code === 200 && res.data) {
                setMetaConnectStatus(res.data.metaConnectedStatus);
            }
        } catch (error) {
            console.error('Error fetching Meta status:', error);
        }
    }, []);

    // Auto-initialize when authenticated
    useEffect(() => {
        if (isAuthenticated) {
            fetchAdAccountDetails();
            fetchWabaDetails();
            fetchMetaStatus();
        }
    }, [isAuthenticated, fetchAdAccountDetails, fetchWabaDetails, fetchMetaStatus, metaConnectStatus]);

    const clearSelections = () => {
        setSelectedAdAccount(null);
        setSelectedFbPage(null);
        setSelectedWaba(null);
    };

    return (
        <MetaSetupContext.Provider
            value={{
                selectedAdAccount,
                setSelectedAdAccount,
                selectedFbPage,
                setSelectedFbPage,
                selectedWaba,
                setSelectedWaba,
                wabaList,
                orgWabaData,
                loadingWaba,
                fetchWabaDetails,
                fetchAdAccountDetails,
                adAccountList,
                loadingAdAccounts,
                metaConnectStatus,
                setMetaConnectStatus,
                fetchMetaStatus,
                clearSelections,
            }}
        >
            {children}
        </MetaSetupContext.Provider>
    );
};

export const useMetaSetup = (): MetaSetupContextType => {
    const context = useContext(MetaSetupContext);
    if (!context) {
        throw new Error('useMetaSetup must be used within a MetaSetupProvider');
    }
    return context;
};
